import React, {
  CSSProperties,
  JSX,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { GetServerSideProps } from "next";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import { A11y, Mousewheel } from "swiper/modules";
import { generatePageId } from "@pepdirect/v3/helpers/analyticsLogger";
import { PAGE_TYPE_ENUM } from "@pepdirect/v3/types";

import { assetsRoot } from "config";
import { LogContext } from "context/log";
import { CANONICAL_URL_BASE } from "constants/meta";
import {
  FALL_FOR_FLAVOR_CARD_IMAGE,
  getPdpProductByHandle,
  PDP_PRODUCTS,
  PdpProduct,
} from "constants/pdpProducts";
import {
  BenefitLove,
  BenefitPrebiotics,
  BenefitCaffeine,
  CertSeals,
  BenefitSugar,
  CircleArrowLeft,
  CircleArrowRight,
  LogoCostco,
  LogoKroger,
  LogoPublix,
  LogoTarget,
  LogoWalmart,
  LogoWholeFoods,
  Sparkle,
  WaveBandBottomGold,
  WaveBandTop,
  WaveGoldToPink,
  WavePinkBottom,
  WaveScallop,
} from "components/PdpArt";
import { SmartButton, SmartCommerceCarousel } from "components/SmartCommerce";
import { useCommerceProductView } from "hooks/useCommerceAnalytics";

import st from "./product.module.scss";

const A = `${assetsRoot}/poppi`;

// live's single-flavor PDPs show a tile strip that jumps between flavors
// live's hero flavor strip: 18 singles then the current packs, fixed order,
// identical on every single-flavor PDP (packs don't render the strip);
// it's a single-row 500px carousel of 70×70 icon tiles (flavor icon on the
// product's primary, 5px gaps, 15px edge padding, always starting at
// sour-apple — live never scrolls to the current flavor, the secondary-color
// ring marks it) with 32px circle arrows floated 50px outside the strip
const STRIP_ORDER = [
  "sour-apple",
  "shirley-temple",
  "orange-cream",
  "strawberry-lemon",
  "wild-berry-12oz",
  "lemon-lime",
  "cream-soda",
  "rootbeer",
  "alpine-blast",
  "doc-pop",
  "orange",
  "cherry-cola",
  "ginger-lime",
  "raspberry-rose",
  "grape",
  "cherry-limeade",
  "watermelon",
  "classic-cola",
  "fall-for-flavor",
  "major-mix",
  "crowd-pleasers",
  "functional-favorites",
  "short-list",
  "trend-setters",
  "classics",
];

// the pack tiles in the strip use dedicated icons the singles' flavors[]
// don't carry — mirrored from live to the CDN flavor-icons set (2026-08-25)
const PACK_STRIP_ICONS = new Map<string, string>([
  ["fall-for-flavor", FALL_FOR_FLAVOR_CARD_IMAGE],
  ["major-mix", `${A}/pdp/flavor-icons/major-mix.png`],
  ["crowd-pleasers", `${A}/pdp/flavor-icons/crowd-pleasers.jpg`],
  ["functional-favorites", `${A}/pdp/flavor-icons/functional-favorites.png`],
  ["short-list", `${A}/pdp/flavor-icons/short-list.png`],
  ["trend-setters", `${A}/pdp/flavor-icons/trend-setters.png`],
  ["classics", `${A}/pdp/flavor-icons/classics.png`],
]);

// live's "shop now" dropdown: the current singles lineup, each with its own
// two-tone theme (identical to the collection tokens) and can render
// live's cross-sell carousel: every variety pack, the current one first
const PACK_ORDER = [
  "fall-for-flavor",
  "major-mix",
  "functional-favorites",
  "crowd-pleasers",
  "classics",
  "short-list",
  "trend-setters",
];

function requirePdpProduct(handle: string): PdpProduct {
  const product = getPdpProductByHandle(handle);
  if (!product) {
    throw new Error(`Unknown product in static PDP order: ${handle}`);
  }
  return product;
}

const PACK_CARDS = PACK_ORDER.map(requirePdpProduct);
const STRIP_PRODUCTS = STRIP_ORDER.map((handle) => {
  const product = requirePdpProduct(handle);
  return {
    handle,
    product,
    icon: PACK_STRIP_ICONS.get(handle) ?? product.flavors[0].icon,
  };
});

const BENEFITS = [
  { icon: BenefitSugar, label: "5g Sugar" },
  { icon: BenefitLove, label: "Ingredients You Can Love" },
  { icon: BenefitPrebiotics, label: "Prebiotics" },
];

// caffeinated singles append live's 4th badge
const CAFFEINE_BADGE = { icon: BenefitCaffeine, label: "Natural Caffeine" };

const RETAILER_LOGOS = [
  { name: "Costco", Logo: LogoCostco, w: 200 },
  { name: "Kroger", Logo: LogoKroger, w: 200 },
  { name: "Publix", Logo: LogoPublix, w: 200 },
  { name: "Target", Logo: LogoTarget, w: 200 },
  { name: "Whole Foods Market", Logo: LogoWholeFoods, w: 120 },
  { name: "Walmart", Logo: LogoWalmart, w: 200 },
];

// fallback lifestyle tiles for products without harvested strip stills
const TASTE_FALLBACK = [
  `${A}/lifestyle/homepage-13.jpg`,
  `${A}/lifestyle/homepage-11.jpg`,
  `${A}/lifestyle/homepage-10.jpg`,
  `${A}/lifestyle/homepage-17.jpg`,
  `${A}/lifestyle/homepage-20.jpg`,
];

function NutritionCard({
  n,
}: {
  n: import("constants/pdpProducts").PdpNutrition;
}): JSX.Element {
  return (
    <div className={st.nutriCard}>
      <p className={st.nutriFlavor}>{n.flavor}</p>
      <p className={st.nutriServing}>Serving Size 1 can</p>
      <div className={st.nutriCalRow}>
        <div>
          <p className={st.nutriSm}>Amount per serving</p>
          <p className={st.nutriCalWord}>Calories</p>
        </div>
        <p className={st.nutriCalNum}>{n.calories}</p>
      </div>
      <p className={st.nutriDaily}>
        <strong>% Daily Value*</strong>
      </p>
      <div className={st.nutriRow}>
        <p>
          <strong>Total Fat</strong> {n.totalFat}
        </p>
        <p>
          <strong>0%</strong>
        </p>
      </div>
      <div className={st.nutriRow}>
        <p>
          <strong>Sodium</strong> {n.sodium}
        </p>
        <p>
          <strong>0%</strong>
        </p>
      </div>
      <div className={st.nutriRow}>
        <p>
          <strong>Total Carbohydrate</strong> {n.carbs}
        </p>
        <p>
          <strong>{n.carbsPct}</strong>
        </p>
      </div>
      <div className={st.nutriRow}>
        <p className={st.indent1}>Dietary Fiber {n.fiber}</p>
        <p>
          <strong>{n.fiberPct}</strong>
        </p>
      </div>
      <div className={st.nutriRow}>
        <p className={st.indent1}>Total Sugars {n.sugars}</p>
      </div>
      <div className={st.nutriRow}>
        <p className={st.indent2}>Includes {n.addedSugars} Added Sugars</p>
        <p>
          <strong>{n.addedSugarsPct}</strong>
        </p>
      </div>
      <div className={`${st.nutriRow} ${st.nutriRowLast}`}>
        <p>
          <strong>Protein</strong> {n.protein}
        </p>
      </div>
      <p className={st.nutriNote}>
        Not a significant source of saturated fat, trans fat, cholesterol,
        vitamin D, calcium, iron, and potassium.
      </p>
    </div>
  );
}

interface ProductPageProps {
  product: PdpProduct;
}

export const getServerSideProps: GetServerSideProps<ProductPageProps> = async ({
  params,
}) => {
  const product = getPdpProductByHandle(params?.handle);
  if (!product) return { notFound: true };
  return { props: { product } };
};

export default function ProductPage({
  product,
}: ProductPageProps): JSX.Element {
  const { useLogPageView } = useContext(LogContext);
  useLogPageView(
    {
      pageType: PAGE_TYPE_ENUM.product,
      pageTitle: `${product.name} – poppi`,
      id: generatePageId(`products-${product.handle}-page`),
    },
    undefined,
    undefined,
    product.handle
  );
  useCommerceProductView(product.handle, {
    viewKey: `/products/${product.handle}`,
  });
  const [gallery, setGallery] = useState<SwiperType | null>(null);
  // once the visitor swipes or pages the gallery we stop re-seating it on
  // slide 1 (see onGalleryImgLoad below)
  const galleryTouched = useRef(false);
  const [nutriSwiper, setNutriSwiper] = useState<SwiperType | null>(null);
  // live: clicking an "Includes" tile reveals that flavor's detail card
  const [openFlavor, setOpenFlavor] = useState<string | null>(null);
  // "Taste the Obsession" video lightbox (live: fullscreen player over a
  // black overlay when a feed tile is clicked)
  const [openVideo, setOpenVideo] = useState<{
    caption: string;
    src: string;
    thumb: string;
  } | null>(null);
  // live's hover previews are sticky: the hovered tile plays inline (play
  // icon hidden) and keeps playing until ANOTHER tile is hovered
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const tasteStripRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const vids = tasteStripRef.current?.querySelectorAll("video");
    vids?.forEach((vid) => {
      if (vid.dataset.src === previewSrc) {
        vid.play().catch(() => undefined);
      } else {
        vid.pause();
        vid.currentTime = 0;
      }
    });
  }, [previewSrc]);

  useEffect(() => {
    if (!openVideo) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenVideo(null);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [openVideo]);
  // Reviews were intentionally removed following legal feedback (2026-08-25).
  // Do not restore this state or the commented section without legal approval.
  // const [revSwiper, setRevSwiper] = useState<SwiperType | null>(null);
  const [packSwiper, setPackSwiper] = useState<SwiperType | null>(null);
  const [flavorSwiper, setFlavorSwiper] = useState<SwiperType | null>(null);
  const [packPage, setPackPage] = useState(0);
  // live's fixed bottom CTA slides up once the hero is scrolled out of view
  const [ctaVisible, setCtaVisible] = useState(false);
  const heroRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) =>
      setCtaVisible(!entry.isIntersecting)
    );
    observer.observe(hero);
    return () => observer.disconnect();
  }, []);

  const themeStyle = {
    "--color-primary": product.primary,
    "--color-secondary": product.secondary,
  } as CSSProperties;

  // Lift the product theme to the document root so the global header + footer
  // (rendered outside this page) re-theme to the product's colours. Live: bar
  // in primary with secondary links, footer inverted (secondary bg).
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--color-primary", product.primary);
    root.style.setProperty("--color-secondary", product.secondary);
    root.style.setProperty("--page-top", "80px");
    root.style.setProperty("--page-bg", product.primary);
    root.style.setProperty("--footer-bg", product.secondary);
    root.style.setProperty("--footer-text", product.primary);
    return () => {
      root.style.removeProperty("--color-primary");
      root.style.removeProperty("--color-secondary");
      root.style.removeProperty("--page-top");
      root.style.removeProperty("--page-bg");
      root.style.removeProperty("--footer-bg");
      root.style.removeProperty("--footer-text");
    };
  }, [product.primary, product.secondary]);

  // the slides are natural-width images, but swiper measures the loop
  // geometry at init — before the images have dimensions. Re-measure as each
  // one lands and, until the visitor interacts, keep the strip seated on the
  // first image so it never rests on a partial slide.
  const onGalleryImgLoad = (): void => {
    if (!gallery || gallery.destroyed) return;
    gallery.update();
    if (!galleryTouched.current) gallery.slideToLoop(0, 0);
  };

  const isPack = product.flavors.length > 1;
  // live renders the same static pack order on every PDP (incl. pack PDPs)
  const packCards = PACK_CARDS;
  const tasteTiles = product.tasteTiles ?? TASTE_FALLBACK;
  // live loops just two review cards — pad the loop so swiper can wrap
  // (hidden per brand request 2026-08-25; the review data itself stays in
  // PDP_PRODUCTS so restoring the section below brings everything back)
  // const reviewSlides = [...product.reviews, ...product.reviews, ...product.reviews];

  return (
    <div className={st.pdp} style={themeStyle}>
      <Head>
        <title>{`${product.name} – poppi`}</title>
        <link
          rel="canonical"
          href={`${CANONICAL_URL_BASE}/products/${product.handle}`}
        />
        <meta
          name="description"
          content={product.description}
          key="description"
        />
      </Head>

      {/* ---------------- HERO: copy left, filmstrip gallery right ---------------- */}
      <section className={st.hero} ref={heroRef}>
        <div className={st.packStripBox}>
          <div className={st.packStripTrack}>
            {[0, 1].map((dup) => (
              <span
                className={st.packStripGroup}
                key={dup}
                aria-hidden={dup === 1}
              >
                {Array.from({ length: 4 }).map((_, i) => (
                  <span className={st.packStripItem} key={i}>
                    {product.packSize}
                    <Sparkle className={st.sparkle} />
                  </span>
                ))}
              </span>
            ))}
          </div>
        </div>
        <div className={st.heroLeft}>
          {/* mobile shows the product name as a second bordered marquee */}
          <div
            className={`${st.packStripBox} ${st.nameStripBox}`}
            aria-hidden="true"
          >
            <div className={st.packStripTrack}>
              {[0, 1].map((dup) => (
                <span className={st.packStripGroup} key={dup}>
                  {Array.from({ length: 4 }).map((_, i) => (
                    <span className={st.packStripItem} key={i}>
                      {product.name}
                      <Sparkle className={st.sparkle} />
                    </span>
                  ))}
                </span>
              ))}
            </div>
          </div>

          <h1 className={st.title}>{product.name}</h1>
          <p className={st.desc}>{product.description}</p>

          {/* live's click2cart widget: the SmartCommerce retailer carousel
              (Amazon/Walmart logos + zip box) rendered from the edge API.
              Products without a UPC keep the static Amazon-tile fallback. */}
          {product.upc ? (
            <div className={st.scCarousel}>
              <SmartCommerceCarousel upc={product.upc} />
            </div>
          ) : (
            <div className={st.cartWidget}>
              <a
                className={st.amazonLink}
                href={
                  product.buyUrl ??
                  `https://www.amazon.com/s?k=${encodeURIComponent(
                    `poppi ${product.name}`
                  )}`
                }
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${product.name} on Amazon (opens in a new tab)`}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={`${A}/click2cart/amazon.svg`}
                  alt="Amazon logo"
                  className={st.amazonLogo}
                />
              </a>
              <span className={st.cartWidgetCaption}>
                Click to save in your favorite cart
              </span>
              <button type="button" className={st.changeLocation}>
                <svg
                  viewBox="0 0 12 16"
                  width="11"
                  height="15"
                  aria-hidden="true"
                >
                  <path
                    d="M6 0a6 6 0 0 0-6 6c0 4.2 6 10 6 10s6-5.8 6-10a6 6 0 0 0-6-6Zm0 8.4A2.4 2.4 0 1 1 6 3.6a2.4 2.4 0 0 1 0 4.8Z"
                    fill="currentColor"
                  />
                </svg>
                Change Location
              </button>
            </div>
          )}

          {isPack ? (
            <>
              <h3 className={st.includesHeading}>Includes:</h3>
              <div className={st.tilesWrap}>
                {product.flavors.map((f) => (
                  <button
                    key={f.name}
                    type="button"
                    className={st.tile}
                    style={{ backgroundColor: f.color }}
                    title={f.name}
                    aria-expanded={openFlavor === f.name}
                    onClick={() =>
                      setOpenFlavor((cur) => (cur === f.name ? null : f.name))
                    }
                  >
                    <span className={st.qtyBadge}>{f.qty}</span>
                    <Image
                      src={f.icon}
                      alt={f.name}
                      width={70}
                      height={70}
                      style={{ objectFit: "contain" }}
                    />
                  </button>
                ))}
              </div>
              {/* live's flavor-detail cards: server-rendered for every included
                  flavor but hidden until its tile is clicked (live parity) */}
              <div className={st.flavorDetails}>
                {product.flavors.map((f) => {
                  const single = Object.values(PDP_PRODUCTS).find(
                    (pp) => pp.flavors.length === 1 && pp.name === f.name
                  );
                  if (!single) return null;
                  return (
                    <div
                      key={f.name}
                      className={`${st.flavorDetail} ${
                        openFlavor === f.name ? st.flavorDetailOpen : ""
                      }`}
                      style={{ backgroundColor: single.primary }}
                    >
                      <span className={st.flavorDetailImg}>
                        <Image
                          src={`${assetsRoot}/poppi/collections/${single.handle}.webp`}
                          alt={f.name}
                          width={65}
                          height={122}
                          style={{ objectFit: "contain" }}
                        />
                      </span>
                      <span
                        className={st.flavorDetailContent}
                        style={{ color: single.secondary }}
                      >
                        <strong>{f.name}</strong>
                        {single.description}
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className={st.flavorNav} aria-label="more flavors">
              <Swiper
                className={st.flavorSwiper}
                modules={[A11y, Mousewheel]}
                slidesPerView="auto"
                spaceBetween={5}
                slidesOffsetBefore={15}
                slidesOffsetAfter={15}
                mousewheel={{ forceToAxis: true }}
                onSwiper={setFlavorSwiper}
              >
                {STRIP_PRODUCTS.map(({ handle, product: f, icon }) => {
                  const isActive = handle === product.handle;
                  return (
                    <SwiperSlide key={handle} className={st.flavorSlide}>
                      <Link
                        href={`/products/${handle}`}
                        className={`${st.flavorNavTile} ${
                          isActive ? st.flavorNavActive : ""
                        }`}
                        style={{ backgroundColor: f.primary }}
                        title={f.name}
                        aria-current={isActive ? "page" : undefined}
                      >
                        <Image
                          src={icon}
                          alt={f.name}
                          width={42}
                          height={42}
                          style={{ objectFit: "contain" }}
                        />
                      </Link>
                    </SwiperSlide>
                  );
                })}
              </Swiper>
              <div className={st.flavorArrows}>
                <button
                  type="button"
                  aria-label="previous flavors"
                  onClick={() => flavorSwiper?.slidePrev()}
                >
                  <CircleArrowLeft className={st.flavorArrowIcon} />
                </button>
                <button
                  type="button"
                  aria-label="next flavors"
                  onClick={() => flavorSwiper?.slideNext()}
                >
                  <CircleArrowRight className={st.flavorArrowIcon} />
                </button>
              </div>
            </div>
          )}
        </div>

        <div className={st.heroGallery}>
          <Swiper
            // remount per product: client-side PDP navigation would otherwise
            // keep the old instance's scroll position (live full-page loads
            // always start the filmstrip on image 1)
            key={product.handle}
            className={st.gallerySwiper}
            modules={[A11y]}
            slidesPerView="auto"
            spaceBetween={10}
            slidesOffsetBefore={19}
            loop
            onSwiper={(s) => {
              galleryTouched.current = false;
              setGallery(s);
            }}
            onTouchStart={() => {
              galleryTouched.current = true;
            }}
          >
            {(product.galleryStrip ?? product.gallery).map((entry, i) =>
              Array.isArray(entry) ? (
                <SwiperSlide
                  key={entry[0]}
                  className={`${st.gallerySlide} ${st.gallerySlidePair}`}
                >
                  {entry.map((img, j) => (
                    <img
                      key={img}
                      src={img}
                      alt={`${product.name} ${i + 1}`}
                      className={st.galleryPairImg}
                      onLoad={onGalleryImgLoad}
                      // First image in the strip is the LCP element — fetch it
                      // eagerly at high priority; everything else defers.
                      loading={i === 0 && j === 0 ? "eager" : "lazy"}
                      fetchPriority={i === 0 && j === 0 ? "high" : "auto"}
                      decoding={i === 0 && j === 0 ? "auto" : "async"}
                    />
                  ))}
                </SwiperSlide>
              ) : (
                <SwiperSlide key={entry} className={st.gallerySlide}>
                  <img
                    src={entry}
                    alt={`${product.name} ${i + 1}`}
                    className={st.galleryImg}
                    style={
                      product.galleryFit === "contain"
                        ? { objectFit: "contain" }
                        : undefined
                    }
                    onLoad={onGalleryImgLoad}
                    // First slide is the LCP element — fetch it eagerly at high
                    // priority; the rest of the filmstrip loads lazily.
                    loading={i === 0 ? "eager" : "lazy"}
                    fetchPriority={i === 0 ? "high" : "auto"}
                    decoding={i === 0 ? "auto" : "async"}
                  />
                </SwiperSlide>
              )
            )}
          </Swiper>
          <div className={st.galleryArrows}>
            <button
              type="button"
              aria-label="previous image"
              onClick={() => {
                galleryTouched.current = true;
                gallery?.slidePrev();
              }}
            >
              <CircleArrowLeft className={st.circleArrow} />
            </button>
            <button
              type="button"
              aria-label="next image"
              onClick={() => {
                galleryTouched.current = true;
                gallery?.slideNext();
              }}
            >
              <CircleArrowRight className={st.circleArrow} />
            </button>
          </div>
        </div>
      </section>

      {/* ---------------- pink benefits band (the only add-to-cart lives in
           the fixed bottom CTA now) ---------------- */}
      <section className={st.buyBand}>
        <WaveBandTop className={st.bandTopWave} />
        <div className={st.benefits}>
          {[
            ...BENEFITS,
            ...(product.caffeineBadge ? [CAFFEINE_BADGE] : []),
          ].map(({ icon: Icon, label }) => (
            <div key={label} className={st.benefit}>
              <Icon className={st.benefitIcon} />
              <span className={st.benefitLabel}>{label}</span>
            </div>
          ))}
        </div>
        <div className={st.bandBottomWrap}>
          <WaveBandBottomGold className={st.bandBottomWave} />
        </div>
      </section>

      {/* ---------------- nutrition ---------------- */}
      <section className={st.nutrition}>
        {!isPack && product.nutrition[0] && (
          <div className={st.nutriSingleRow}>
            <NutritionCard n={product.nutrition[0]} />
            <div className={st.nutriSingleText}>
              <p className={st.ingredients}>
                Ingredients: {product.nutrition[0].ingredients}
              </p>
              {product.nutrition[0].notes.map((note) => (
                <p key={note} className={st.ingredientNote}>
                  {note.startsWith("Contains") ? <strong>{note}</strong> : note}
                </p>
              ))}
              <CertSeals className={st.certs} />
            </div>
          </div>
        )}
        {isPack && (
          <Swiper
            className={st.nutriSwiper}
            modules={[A11y]}
            slidesPerView="auto"
            spaceBetween={12}
            slidesOffsetBefore={27}
            slidesOffsetAfter={27}
            breakpoints={{
              1024: { slidesOffsetBefore: 57, slidesOffsetAfter: 57 },
            }}
            onSwiper={setNutriSwiper}
          >
            {product.nutrition.map((n) => (
              <SwiperSlide key={n.flavor} className={st.nutriSlide}>
                <NutritionCard n={n} />
                <p className={st.ingredients}>Ingredients: {n.ingredients}</p>
                {n.notes.map((note) => (
                  <p key={note} className={st.ingredientNote}>
                    {note.startsWith("Contains") ? (
                      <strong>{note}</strong>
                    ) : (
                      note
                    )}
                  </p>
                ))}
              </SwiperSlide>
            ))}
          </Swiper>
        )}
        {isPack && (
          <div className={st.nutriFootRow}>
            {product.handle === "bombshells-variety-pack" ? (
              <img
                src={`${A}/pdp/bombshells/certs.png`}
                alt="Certified gluten free, kosher, Non-GMO Project verified"
                className={st.certs}
                loading="lazy"
                decoding="async"
              />
            ) : (
              <CertSeals className={st.certs} />
            )}
            <div className={st.carouselArrows}>
              <button
                type="button"
                aria-label="Previous slide"
                onClick={() => nutriSwiper?.slidePrev()}
              >
                <CircleArrowLeft className={st.circleArrow} />
              </button>
              <button
                type="button"
                aria-label="Next slide"
                onClick={() => nutriSwiper?.slideNext()}
              >
                <CircleArrowRight className={st.circleArrow} />
              </button>
            </div>
          </div>
        )}
        <Link href="/pages/whypoppi" className={st.faqPill}>
          FAQ
          <CircleArrowLeft className={st.faqArrow} />
        </Link>
      </section>

      {/* ---------------- Taste the Obsession (live omits it on sour-apple) ---------------- */}
      {!product.hideTaste && (
        <section className={st.taste}>
          <WaveBandTop className={st.tasteTopWave} />
          <div className={st.tasteBody}>
            <h2 className={st.tasteHeading}>
              Taste {isPack ? "the" : "The"} Obsession
            </h2>
            {product.tasteVideos?.length ? (
              <div className={st.tasteStrip} ref={tasteStripRef}>
                {product.tasteVideos.map((v) => (
                  <button
                    key={v.src}
                    type="button"
                    className={`${st.tasteTile} ${st.tasteVideoTile}`}
                    onClick={() => setOpenVideo(v)}
                    onMouseEnter={() => setPreviewSrc(v.src)}
                    aria-label={`play video: ${v.caption}`}
                  >
                    {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                    <video
                      className={st.tastePreview}
                      src={v.src}
                      data-src={v.src}
                      poster={v.thumb}
                      muted
                      loop
                      playsInline
                      preload="none"
                    />
                    <span
                      className={`${st.tastePlay} ${
                        previewSrc === v.src ? st.tastePlayHidden : ""
                      }`}
                      aria-hidden="true"
                    >
                      <svg viewBox="0 0 24 24" fill="none">
                        <circle
                          cx="12"
                          cy="12"
                          r="11"
                          fill="rgba(0, 0, 0, 0.45)"
                        />
                        <path d="M9.5 7.5v9l7-4.5-7-4.5Z" fill="#fff" />
                      </svg>
                    </span>
                    <span className={st.tasteCaption}>{v.caption}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className={st.tasteStrip}>
                {tasteTiles.map((img, i) => (
                  <span key={img} className={st.tasteTile}>
                    <Image
                      src={img}
                      alt={`${product.name} moment ${i + 1}`}
                      fill
                      sizes="240px"
                      style={{ objectFit: "cover" }}
                      loading="lazy"
                    />
                  </span>
                ))}
              </div>
            )}
          </div>
          <WavePinkBottom className={st.tasteBottomWave} />
          {openVideo &&
            createPortal(
              // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
              <div
                className={st.videoLightbox}
                role="dialog"
                aria-modal="true"
                aria-label={openVideo.caption}
                onClick={() => setOpenVideo(null)}
              >
                <button
                  type="button"
                  className={st.videoClose}
                  aria-label="close video"
                  onClick={() => setOpenVideo(null)}
                >
                  &times;
                </button>
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video
                  src={openVideo.src}
                  poster={openVideo.thumb}
                  controls
                  autoPlay
                  playsInline
                  onClick={(e) => e.stopPropagation()}
                />
              </div>,
              document.body
            )}
        </section>
      )}

      {/* ---------------- reviews (intentionally removed) ----------------
          Removed following legal feedback (2026-08-25). Do not restore this
          section or its Swiper state without explicit legal approval.

      {product.reviews.length > 0 && (
      <section className={st.reviews}>
        <div className={st.reviewsRow}>
          <h3 className={st.reviewsHeading}>Here&apos;s What Customers Are Saying</h3>
          <div className={st.reviewsRail}>
            <Swiper
              className={st.revSwiper}
              modules={[A11y]}
              slidesPerView="auto"
              spaceBetween={15}
              loop
              onSwiper={setRevSwiper}
            >
              {reviewSlides.map((r, i) => (
                <SwiperSlide key={`${r.author}-${i}`} className={st.revSlide}>
                  <figure className={st.revCard}>
                    <figcaption className={st.revTitle}>{r.title}</figcaption>
                    <blockquote className={st.revText}>{r.text}</blockquote>
                    <span className={st.revAuthor}>- {r.author}</span>
                  </figure>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
        <div className={`${st.carouselArrows} ${st.revArrows}`}>
          <button type="button" aria-label="Previous slide" onClick={() => revSwiper?.slidePrev()}>
            <CircleArrowLeft className={st.circleArrow} />
          </button>
          <button type="button" aria-label="Next slide" onClick={() => revSwiper?.slideNext()}>
            <CircleArrowRight className={st.circleArrow} />
          </button>
        </div>
      </section>
      )}
      */}

      {/* ---------------- variety packs cross-sell ---------------- */}
      <section className={st.packsBand}>
        <WaveGoldToPink className={st.packsTopWave} />
        <div className={st.packsBody}>
          <p className={st.packsLabel}>Variety Packs</p>
          <h3 className={st.packsHeading}>
            tastes like you’ll need more than one
          </h3>
          <Swiper
            className={st.packsSwiper}
            modules={[A11y]}
            slidesPerView="auto"
            spaceBetween={0}
            onSwiper={setPackSwiper}
            onSlideChange={(s) => setPackPage(s.activeIndex)}
          >
            {packCards.map((pdp) => {
              const isSelf = pdp.handle === product.handle;
              return (
                <SwiperSlide key={pdp.handle} className={st.packSlide}>
                  <div
                    className={st.packCardInner}
                    style={
                      {
                        "--pack-primary": pdp.primary,
                        "--pack-secondary": pdp.secondary,
                      } as CSSProperties
                    }
                  >
                    <div className={st.packArch}>
                      <div className={st.packDisc}>
                        <div className={st.packClip}>
                          <Link
                            href={`/products/${pdp.handle}`}
                            className={st.packCanLink}
                          >
                            {/* Preserve the artwork's natural ratio and crop only at the rounded lower edge, as production does. */}
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img
                              src={pdp.cardImage ?? `${A}/collections/${pdp.handle}.webp`}
                              alt={`poppi ${pdp.name}`}
                              className={st.packCan}
                              loading="lazy"
                              decoding="async"
                            />
                          </Link>
                        </div>
                      </div>
                    </div>
                    <div className={st.packBar}>
                      <Link href={`/products/${pdp.handle}`}>{pdp.name}</Link>
                    </div>
                  </div>
                  {isSelf && product.buyUrl ? (
                    <a
                      className={st.packPill}
                      href={product.buyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        backgroundColor: pdp.primary,
                        color: pdp.secondary,
                        borderColor: pdp.secondary,
                        boxShadow: `5px 5px 0 0 ${pdp.secondary}`,
                      }}
                    >
                      buy now
                      <svg
                        viewBox="0 0 16 16"
                        width="14"
                        height="14"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708"
                          fill="currentColor"
                        />
                      </svg>
                    </a>
                  ) : pdp.upc ? (
                    // live parity: each cross-sell card carts through its own
                    // SmartButton (retailer popup), themed to the pack colours
                    <SmartButton
                      upc={pdp.upc}
                      productName={pdp.name}
                      className={st.packPillSc}
                      style={
                        {
                          "--sc-bg": pdp.primary,
                          "--sc-fg": pdp.secondary,
                          "--color-primary": pdp.primary,
                          "--color-secondary": pdp.secondary,
                        } as CSSProperties
                      }
                    />
                  ) : (
                    <Link
                      href={`/products/${pdp.handle}`}
                      className={st.packPill}
                      style={{
                        backgroundColor: pdp.primary,
                        color: pdp.secondary,
                        borderColor: pdp.secondary,
                        boxShadow: `5px 5px 0 0 ${pdp.secondary}`,
                      }}
                    >
                      view product
                    </Link>
                  )}
                </SwiperSlide>
              );
            })}
          </Swiper>
          <div className={st.packsFootRow}>
            <div
              className={st.packsDots}
              aria-label="Choose a variety pack slide"
            >
              {packCards.map((pdp, i) => (
                <button
                  key={pdp.handle}
                  type="button"
                  className={`${st.packsDot} ${
                    packPage === i ? st.packsDotActive : ""
                  }`}
                  aria-label={`Go to ${pdp.name}`}
                  aria-current={packPage === i ? "true" : undefined}
                  onClick={() => packSwiper?.slideTo(i)}
                />
              ))}
            </div>
            <div className={st.carouselArrows}>
              <button
                type="button"
                aria-label="Previous slide"
                onClick={() => packSwiper?.slidePrev()}
              >
                <CircleArrowLeft className={st.circleArrow} />
              </button>
              <button
                type="button"
                aria-label="Next slide"
                onClick={() => packSwiper?.slideNext()}
              >
                <CircleArrowRight className={st.circleArrow} />
              </button>
            </div>
          </div>
        </div>
        <div className={st.bandBottomWrap}>
          <WaveBandBottomGold className={st.bandBottomWave} />
        </div>
      </section>

      {/* ---------------- retailers ---------------- */}
      <section className={st.retailers}>
        <h2 className={st.retailersHeading}>we’re popping up near you</h2>
        <div className={st.retailerRow}>
          {RETAILER_LOGOS.map(({ name, Logo, w }) => (
            <span
              key={name}
              className={st.retailerLogo}
              style={{ width: w }}
              title={name}
            >
              <Logo />
            </span>
          ))}
        </div>
      </section>

      {/* mobile: live pins the CTA to the viewport bottom under a scallop crest */}
      {/* live's fixed-cta: pinned under a scallop crest, translated offscreen
          until the hero scrolls away, then it slides up from the bottom */}
      <div
        className={`${st.fixedCta} ${ctaVisible ? st.fixedCtaOn : ""}`}
        aria-hidden={!ctaVisible}
      >
        <WaveScallop
          className={st.fixedCtaScallop}
          fill="var(--color-secondary)"
        />
        <div className={st.fixedCtaRow}>
          <span className={st.fixedCtaName}>{product.name}</span>
          {product.upc ? (
            <SmartButton
              upc={product.upc}
              productName={product.name}
              className={st.scFixedBtn}
              tabbable={ctaVisible}
            />
          ) : product.buyUrl ? (
            <a
              className={st.buyBtn}
              href={product.buyUrl}
              target="_blank"
              rel="noopener noreferrer"
              tabIndex={ctaVisible ? 0 : -1}
            >
              buy now
            </a>
          ) : (
            <Link
              href="/pages/find-poppi"
              className={st.buyBtn}
              tabIndex={ctaVisible ? 0 : -1}
            >
              find poppi
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
